import { useQuery } from "@tanstack/react-query";
import { List, Typography, Box } from "@mui/material";
import { useEffect } from "react";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import TaskItem from "@/modules/meetings/components/tasks/task-item";
import { getTasksDashboard } from "@/modules/tasks/api";
import { TaskStatuses } from "@/modules/tasks/enums";
import { TasksResponse } from "@/modules/tasks/dtos";
import useMarkTaskDone from "@/modules/tasks/hooks/use-mark-task-done.hook";
import AddNewTask from "@/modules/tasks/components/add-task";
import ClipboardListIcon from "@/modules/common/components/ui-kit/icons/clipboard-list-icon";
import { excludeTaskTypes } from "@/modules/tasks/utils";

interface TasksProps {
  onTasksStatusChange?: (hasTasks: boolean) => void;
}

export default function Tasks({ onTasksStatusChange }: TasksProps) {
  const filters = {
    ...excludeTaskTypes(),
    onlyU: true,
  };
  const pagination = { page: 1, pageSize: 10 };

  const {
    data: allTasksData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["tasks-dashboard", { filters, pagination }],
    queryFn: () =>
      getTasksDashboard({
        filters,
        pagination,
      }),
  });

  const markDoneMutation = useMarkTaskDone({
    queryKeys: ["tasks-dashboard", "task-statistics", "tasks-count"],
    successMessage: "Task completed successfully",
  });

  const handleCheckboxChange = (task: TasksResponse) => {
    if (task.status !== TaskStatuses.Completed) {
      markDoneMutation.mutate({
        id: task.uid,
      });
    }
  };

  const isTaskCompleting = (taskUid: string) => {
    return (
      markDoneMutation.isPending &&
      markDoneMutation.variables?.id === taskUid
    );
  };

  const allTasks = allTasksData?.data?.data || [];

  // Notify parent component about tasks status
  useEffect(() => {
    if (!isLoading && !isError) {
      onTasksStatusChange?.(allTasks.length > 0);
    }
  }, [allTasks.length, isLoading, isError, onTasksStatusChange]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "40px",
        }}
      >
        <Typography>Loading tasks...</Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "40px",
        }}
      >
        <Typography color="error">Error loading tasks</Typography>
      </Box>
    );
  }

  if (allTasks.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "16px 8px",
          gap: 1,
        }}
      >
        <ClipboardListIcon
          width={24}
          height={24}
          fill={nawyTheme.nawy.colors.semantic.layer.primaryInverse}
        />
        <Typography
          sx={{
            ...nawyTheme.nawy.typography.english.variants.headline.xs,
            color: nawyTheme.nawy.colors.semantic.text.primary,
          }}
        >
          No tasks yet
        </Typography>
        <Typography
          sx={{
            ...nawyTheme.nawy.typography.english.variants.body.md,
            color: nawyTheme.nawy.colors.semantic.text.primary,
          }}
        >
          You don't have any tasks yet.
        </Typography>
        <AddNewTask />
      </Box>
    );
  }

  return (
    <>
      <List sx={{ padding: 0 }}>
        {allTasks.map((task: TasksResponse) => (
          <TaskItem
            key={task.uid}
            task={task}
            onCheckboxChange={handleCheckboxChange}
            isLoading={isTaskCompleting(task.uid)}
          />
        ))}
      </List>
    </>
  );
}
